/**
 * Per-match voice-intent prompt.
 *
 * Asks the scorer on their FIRST visit to a match's coding screen:
 *   "Are you going to use voice coding?"
 *     Yes → run the 60-second calibration gauntlet, then continue.
 *     No  → skip calibration; coding starts immediately.
 *
 * Choice is persisted per match in localStorage (`zcd_voice_intent_<matchId>`)
 * so re-entering the same match never re-prompts. Different match → fresh
 * decision. Founder ask 2026-06-11.
 *
 * Differs from the existing global VoiceCalibrationGauntlet which auto-ran
 * once per browser regardless of match: this prompt is per-match and
 * explicitly opt-in for voice. The gauntlet itself stays the calibration
 * implementation when the operator picks Yes.
 */
import { useRef } from "react";
import { Mic, MicOff, X } from "lucide-react";
import useDialogDismiss from "../../hooks/useDialogDismiss";


const STORAGE_PREFIX = "zcd_voice_intent_";


function storageKey(matchId) {
  return `${STORAGE_PREFIX}${matchId}`;
}


/** "yes" | "no" | null — null = never asked for this match. */
export function getVoiceIntent(matchId) {
  if (!matchId) return null;
  try {
    const v = localStorage.getItem(storageKey(matchId));
    return v === "yes" || v === "no" ? v : null;
  } catch { return null; }
}


export function setVoiceIntent(matchId, value) {
  if (!matchId) return;
  try {
    if (value === "yes" || value === "no") {
      localStorage.setItem(storageKey(matchId), value);
    } else {
      localStorage.removeItem(storageKey(matchId));
    }
  } catch { /* ignore quota / disabled storage */ }
}


/**
 * @param {object} props
 * @param {boolean} props.open  Whether the modal is visible.
 * @param {function} props.onYes  Picked Yes — caller should open calibration.
 * @param {function} props.onNo   Picked No — caller should proceed to coding.
 */
export default function VoiceIntentPrompt({ open, onYes, onNo }) {
  const panelRef = useRef(null);
  // Block-out: this is a gating prompt — Esc / outside-click / hardware
  // back must NOT dismiss without an answer, otherwise the scorer ends
  // up on the coding surface without us knowing their intent.
  useDialogDismiss({
    open,
    onClose: () => {},
    ref: panelRef,
    esc: false,
    historyBack: false,
  });

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4"
      role="presentation"
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="voice-intent-title"
        className="bg-bg-primary border border-border-default rounded-xl shadow-2xl w-full max-w-md p-6 space-y-5"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="space-y-2">
          <div className="flex items-center gap-2 text-button-primary">
            <Mic className="w-5 h-5" />
            <h2 id="voice-intent-title" className="text-lg font-semibold text-text-primary">
              Are you going to use voice coding?
            </h2>
          </div>
          <p className="text-sm text-text-secondary">
            Voice coding lets you call balls out loud — "four cover drive",
            "wide one", "out caught" — instead of tapping the buttons. It
            works best after a short calibration so the system learns your
            accent.
          </p>
        </header>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <button
            onClick={onYes}
            className="flex flex-col items-start gap-2 p-4 rounded-lg border border-button-primary/40 bg-button-primary/10 hover:bg-button-primary/15 text-left transition-colors"
          >
            <div className="flex items-center gap-2 text-button-primary font-semibold">
              <Mic className="w-4 h-4" />
              Yes, use voice
            </div>
            <p className="text-xs text-text-secondary leading-snug">
              Start a 60-second calibration first, then begin coding. You
              only do this once per match.
            </p>
          </button>

          <button
            onClick={onNo}
            className="flex flex-col items-start gap-2 p-4 rounded-lg border border-border-default bg-bg-secondary/40 hover:bg-bg-secondary/70 text-left transition-colors"
          >
            <div className="flex items-center gap-2 text-text-primary font-semibold">
              <MicOff className="w-4 h-4 text-text-secondary" />
              No, tap only
            </div>
            <p className="text-xs text-text-secondary leading-snug">
              Skip calibration and go straight to the coding surface. You
              can still enable voice later from the voice panel.
            </p>
          </button>
        </div>

        <p className="text-[11px] text-text-secondary text-center">
          You'll only see this once for this match.
        </p>
      </div>
    </div>
  );
}
